import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButtonMUI from "@mui/material/IconButton";
import { alpha } from "@mui/material";
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Videocam as VideocamIcon,
} from "@mui/icons-material";
import { MoreVertical } from "lucide-react";
import IconButton from "../../../components/Buttons/IconButton";
import AnnotationMessageBubble from "./AnnotationMessageBubble";
import AnnotationMentionFormattedText from "./AnnotationMentionFormattedText";
import {
  getDarkBubbleFileItemSx,
  getDarkBubbleIconSx,
  getLightBubbleIconSx,
} from "../../../components/Modal/ModalImageEdit/shared/attachmentChipTheme";
import { INPUT_COLOR_BORDER, PRIMARY_COLOR } from "../../../utils/colors";
import {
  ANNOTATIONS_MESSAGE_MENU_ARIA,
  ANNOTATIONS_SIDEBAR_YOU_LABEL,
  BUTTON_DOWNLOAD,
  DATE_HOUR,
  DATE_SHORT,
} from "../../../i18n/keysTranslations";
import { FILE_FORMATS, IMAGE_FORMATS } from "../../../utils/constants";
import { getExtensionFile } from "../../../utils/string";
import { getAnnotationUserColor } from "../utils/annotationUserColors";
import { canManageAnnotationMessage } from "../utils/annotationMessageActions";

//eslint-disable-next-line
export const formatAnnotationCreated = ({ creationTime, t }) => {
  if (creationTime == null || Number.isNaN(Number(creationTime))) return null;

  try {
    const date = new Date(Number(creationTime));
    return `${t(DATE_HOUR, { date })} - ${t(DATE_SHORT, { date })}`;
  } catch {
    return null;
  }
};

const AnnotationFileItem = ({
  file,
  onDownload,
  onOpen,
  isOnDarkBubble = false,
  fileRowTheme = null,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const extension = getExtensionFile(file?.url || file?.name || "");
  const isImage = IMAGE_FORMATS.includes(extension);
  const fileName = file?.name || file?.url?.split("/").pop() || "file";
  const fileIconSx = isOnDarkBubble
    ? getDarkBubbleIconSx({ fontSize: 20 })
    : fileRowTheme
      ? { fontSize: 20, color: `${fileRowTheme.icon} !important` }
      : getLightBubbleIconSx({ fontSize: 20 });
  const downloadIconSx = isOnDarkBubble
    ? getDarkBubbleIconSx({ fontSize: 16 })
    : fileRowTheme
      ? { fontSize: 16, color: `${fileRowTheme.icon} !important` }
      : getLightBubbleIconSx({ fontSize: 16 });
  const loaderColor = isOnDarkBubble ? "#ffffff" : PRIMARY_COLOR;

  const onDownloadClick = async () => {
    if (!file?.url || isDownloading) return;

    setIsDownloading(true);
    try {
      await onDownload({ url: file.url, name: fileName });
    } finally {
      setIsDownloading(false);
    }
  };

  const onOpenClick = () => {
    if (!file?.url || isDownloading) return;
    onOpen({ file });
  };

  return (
    <Box
      onClick={onOpenClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
        cursor: isDownloading ? "default" : "pointer",
        textAlign: "left",
        ...(isOnDarkBubble
          ? getDarkBubbleFileItemSx()
          : fileRowTheme
            ? {
                backgroundColor: fileRowTheme.background,
                border: `1px solid ${fileRowTheme.border}`,
                "&:hover": {
                  backgroundColor: fileRowTheme.backgroundHover,
                },
              }
            : {
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                backgroundColor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }),
      }}
    >
      {isImage ? (
        <Box
          component="img"
          src={file.url}
          alt={fileName}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            flexShrink: 0,
          }}
        >
          {FILE_FORMATS.includes(extension) ? (
            <DescriptionIcon sx={fileIconSx} />
          ) : (
            <VideocamIcon sx={fileIconSx} />
          )}
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: isOnDarkBubble
            ? "#ffffff"
            : fileRowTheme?.text || "text.secondary",
          flex: 1,
          minWidth: 0,
        }}
      >
        {fileName}
      </Typography>
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          onDownloadClick();
        }}
        loading={isDownloading}
        disabled={isDownloading}
        loaderColor={loaderColor}
        tooltipText={BUTTON_DOWNLOAD}
        edge={false}
        sx={{ p: 0, flexShrink: 0 }}
      >
        <DownloadIcon sx={downloadIconSx} />
      </IconButton>
    </Box>
  );
};

const AnnotationMessage = ({
  annotation,
  currentOwnerUid,
  ownersByID = {},
  userColor: userColorProp = null,
  t,
  onDownloadFile,
  onOpenFile,
  onOpenMessageMenu,
}) => {
  const isOwnMessage = annotation.userID === currentOwnerUid;
  const messageRole = isOwnMessage ? "user" : "assistant";
  const files = Array.isArray(annotation.files) ? annotation.files : [];
  const authorLabel = isOwnMessage
    ? t(ANNOTATIONS_SIDEBAR_YOU_LABEL)
    : ownersByID[annotation.userID]?.name?.trim() ||
      ownersByID[annotation.userID]?.email ||
      "";
  const createdLabel = formatAnnotationCreated({
    creationTime: annotation.creationTime,
    t,
  });
  const userColor = !isOwnMessage
    ? userColorProp || getAnnotationUserColor({ userID: annotation.userID })
    : null;
  const canManageMessage = canManageAnnotationMessage({
    annotation,
    currentOwnerUid,
  });

  return (
    <Box
      sx={{
        opacity: annotation.isOptimistic ? 0.85 : 1,
      }}
    >
      <AnnotationMessageBubble
        role={messageRole}
        bubbleColor={userColor?.bubble}
        textColor={userColor?.text}
        actionMenu={
          canManageMessage ? (
            <IconButtonMUI
              className="annotation-message-menu"
              aria-label={t(ANNOTATIONS_MESSAGE_MENU_ARIA)}
              onClick={(event) =>
                onOpenMessageMenu({
                  event,
                  annotationID: annotation.annotationID,
                })
              }
              size="small"
              sx={{
                opacity: 0,
                transition: "opacity 0.15s ease",
                p: 0.25,
                color: "#ffffff",
                backgroundColor: (theme) =>
                  alpha(theme.palette.common.white, 0.15),
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.common.white, 0.25),
                  color: "#ffffff",
                },
              }}
            >
              <MoreVertical size={16} color="#ffffff" strokeWidth={2} />
            </IconButtonMUI>
          ) : null
        }
      >
        {authorLabel ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              display: "block",
              color: isOwnMessage
                ? "primary.contrastText"
                : userColor?.name || "text.secondary",
              opacity: isOwnMessage ? 0.85 : 1,
            }}
          >
            {authorLabel}
          </Typography>
        ) : null}
        {createdLabel ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.75,
              color: isOwnMessage
                ? "primary.contrastText"
                : userColor?.text || "text.secondary",
              opacity: isOwnMessage ? 0.75 : 0.85,
            }}
          >
            {createdLabel}
          </Typography>
        ) : null}
        <AnnotationMentionFormattedText
          text={annotation.text}
          ownersByID={ownersByID}
          variant={isOwnMessage ? "ownBubble" : "otherBubble"}
          textColor={isOwnMessage ? undefined : userColor?.text || undefined}
          component="div"
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.43,
          }}
        />
        {files.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              mt: 1,
            }}
          >
            {files.map((file, fileIndex) => (
              <AnnotationFileItem
                key={`${annotation.annotationID}-${fileIndex}`}
                file={file}
                onDownload={onDownloadFile}
                onOpen={onOpenFile}
                isOnDarkBubble={isOwnMessage}
                fileRowTheme={userColor?.fileRow}
              />
            ))}
          </Box>
        ) : null}
      </AnnotationMessageBubble>
    </Box>
  );
};

export default AnnotationMessage;
